import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Landmark, ShieldCheck, AlertTriangle, Save, CheckCircle2, Loader, Eye, EyeOff, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const FIELD_LABELS = {
  account_holder_name: 'Account Holder Name',
  bank_name: 'Bank Name',
  account_number: 'Account Number',
  iban: 'IBAN',
  swift_bic: 'SWIFT / BIC',
  routing_number: 'Routing Number (ABA)',
  sort_code: 'Sort Code',
  ifsc_code: 'IFSC Code',
  transit_number: 'Transit Number',
  institution_number: 'Institution Number',
  bsb_number: 'BSB Number',
  branch_code: 'Branch Code',
};

const SENSITIVE = new Set(['account_number', 'iban', 'routing_number']);

const PlatformBankSettings = () => {
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('US');
  const [form, setForm] = useState({});
  const [existing, setExisting] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [revealMap, setRevealMap] = useState({});
  const [showFullBank, setShowFullBank] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/admin/platform/me`);
      if (!me.data?.is_super_admin) {
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }
      setIsSuperAdmin(true);
      const [c, b, p] = await Promise.all([
        axios.get(`${API}/admin/platform/bank-countries`),
        axios.get(`${API}/admin/platform/bank-account`),
        axios.get(`${API}/admin/platform/invoice-policy`),
      ]);
      setCountries(c.data?.countries || []);
      const bank = b.data?.bank_account;
      setExisting(b.data);
      setShowFullBank(!!p.data?.show_full_bank_details_on_invoices);
      if (bank) {
        setCountry(bank.country || 'US');
        setForm({ ...bank });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load bank settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const activeCountry = countries.find((c) => c.code === country) || countries.find((c) => c.code === 'other');
  const fields = activeCountry?.fields || [];

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    // Keep holder/bank, clear country-specific identifiers
    setForm((f) => ({
      account_holder_name: f.account_holder_name || '',
      bank_name: f.bank_name || '',
      notes: f.notes || '',
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { country, ...form };
      const r = await axios.put(`${API}/admin/platform/bank-account`, payload);
      setExisting(r.data);
      setForm({ ...r.data.bank_account });
      toast.success('Bank account saved — please confirm below to activate payouts.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save bank account');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/admin/platform/bank-account/confirm`);
      toast.success('Payout bank account confirmed and active.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleTogglePolicy = async (nextVal) => {
    setPolicySaving(true);
    try {
      const r = await axios.put(`${API}/admin/platform/invoice-policy`, {
        show_full_bank_details_on_invoices: nextVal,
      });
      const savedVal = !!r.data?.show_full_bank_details_on_invoices;
      setShowFullBank(savedVal);
      toast.success(savedVal
        ? 'Customer invoices will now show FULL bank details.'
        : 'Customer invoices will show only the last 4 digits.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update invoice policy');
    } finally {
      setPolicySaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12" data-testid="platform-bank-loading">
        <div className="text-slate-600 font-ibm">Loading…</div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="p-6 md:p-12 max-w-3xl" data-testid="platform-bank-forbidden">
        <div className="rounded-sm border border-red-200 bg-red-50 text-red-900 p-6 flex items-start gap-3">
          <AlertTriangle size={24} className="text-red-700 mt-0.5" />
          <div>
            <h2 className="font-chivo font-bold text-xl mb-1">Platform-owner access required</h2>
            <p className="font-ibm text-sm">
              The payout bank account can only be configured by the platform owner (super-admin). Please contact your administrator if you believe this is a mistake.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isConfirmed = !!existing?.is_confirmed;
  const isConfigured = !!existing?.configured;

  return (
    <div className="p-6 md:p-12 max-w-4xl" data-testid="platform-bank-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center gap-3">
          <Landmark size={32} className="text-slate-900" />
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="platform-bank-title">
              Payout Bank Account
            </h1>
            <p className="text-slate-600 mt-1 font-ibm text-sm">
              Where subscription revenue is deposited. Visible to platform owner only.
            </p>
          </div>
        </div>

        {/* Status banner */}
        {isConfigured && isConfirmed && (
          <div className="mb-6 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 flex items-center gap-3" data-testid="platform-bank-status-confirmed">
            <ShieldCheck size={20} className="text-emerald-700" />
            <div className="flex-1">
              <p className="font-ibm font-semibold text-sm">Payout account active</p>
              <p className="font-ibm text-xs text-emerald-800/80">
                Subscription deposits will be routed to {existing.bank_account?.bank_name} ····{(existing.bank_account?.account_number || existing.bank_account?.iban || '').replace(/\s/g, '').slice(-4)}.
              </p>
            </div>
          </div>
        )}
        {isConfigured && !isConfirmed && (
          <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="platform-bank-status-pending">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className="text-amber-700" />
              <div>
                <p className="font-ibm font-semibold text-sm">Confirmation required</p>
                <p className="font-ibm text-xs text-amber-800/90">
                  Review the details below and confirm to activate this account for subscription payouts.
                </p>
              </div>
            </div>
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              data-testid="platform-bank-confirm-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            >
              {confirming ? <><Loader size={14} className="mr-2 animate-spin" /> Confirming…</> : <><CheckCircle2 size={14} className="mr-2" /> Confirm account</>}
            </Button>
          </div>
        )}
        {!isConfigured && (
          <div className="mb-6 rounded-sm border border-slate-200 bg-slate-50 p-4 flex items-center gap-3" data-testid="platform-bank-status-empty">
            <AlertTriangle size={20} className="text-slate-600" />
            <p className="font-ibm text-sm text-slate-700">
              No payout bank account is configured yet. Set one below — until then, subscribers will see &ldquo;Pending&rdquo;.
            </p>
          </div>
        )}

        <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="platform-bank-form">
          {/* Country */}
          <div>
            <Label htmlFor="country" className="text-slate-900 font-ibm font-semibold text-sm">Country</Label>
            <select
              id="country"
              value={country}
              onChange={handleCountryChange}
              data-testid="platform-bank-country"
              className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 font-ibm text-sm bg-white"
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Dynamic fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => {
              const isSensitive = SENSITIVE.has(f);
              const reveal = revealMap[f];
              const inputType = isSensitive && !reveal ? 'password' : 'text';
              return (
                <div key={f} className={fields.length % 2 === 1 && f === fields[fields.length - 1] ? 'md:col-span-2' : ''}>
                  <Label htmlFor={`field-${f}`} className="text-slate-900 font-ibm font-semibold text-sm">
                    {FIELD_LABELS[f] || f}
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id={`field-${f}`}
                      type={inputType}
                      value={form[f] || ''}
                      onChange={(e) => handleChange(f, e.target.value)}
                      autoComplete="off"
                      data-testid={`platform-bank-field-${f}`}
                      className="border-slate-300 rounded-sm font-ibm text-sm pr-10"
                    />
                    {isSensitive && (form[f] || '').length > 0 && (
                      <button
                        type="button"
                        aria-label={reveal ? 'Hide' : 'Show'}
                        onClick={() => setRevealMap((m) => ({ ...m, [f]: !m[f] }))}
                        data-testid={`platform-bank-reveal-${f}`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      >
                        {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-slate-900 font-ibm font-semibold text-sm">Notes (optional)</Label>
            <textarea
              id="notes"
              value={form.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              maxLength={500}
              data-testid="platform-bank-notes"
              className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 font-ibm text-sm"
              placeholder="Internal reminder, e.g. 'primary USD payout'"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <p className="text-xs font-ibm text-slate-500">
              Editing any field will <strong>reset the confirmation status</strong>. You&apos;ll need to confirm again.
            </p>
            <Button
              type="submit"
              disabled={saving}
              data-testid="platform-bank-save-button"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            >
              {saving ? <><Loader size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save bank account</>}
            </Button>
          </div>
        </form>

        {/* Customer-invoice PDF policy toggle */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6" data-testid="invoice-policy-section">
          <div className="flex items-start gap-4">
            <FileText size={22} className="text-slate-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-chivo font-bold text-slate-900 text-base mb-1">Customer Invoice PDFs</h3>
              <p className="font-ibm text-sm text-slate-600 mb-4 leading-relaxed">
                Control how much of the payout bank account is visible on the
                downloadable invoice PDFs that customers receive after paying.
              </p>

              <label className="flex items-start gap-3 cursor-pointer p-3 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors" data-testid="invoice-policy-toggle-label">
                <input
                  type="checkbox"
                  checked={showFullBank}
                  onChange={(e) => handleTogglePolicy(e.target.checked)}
                  disabled={policySaving}
                  data-testid="invoice-policy-toggle"
                  className="mt-1 h-4 w-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-900"
                />
                <div className="flex-1">
                  <p className="font-ibm font-semibold text-sm text-slate-900">
                    Show full bank details on customer invoice PDFs
                  </p>
                  <p className="font-ibm text-xs text-slate-500 mt-1 leading-relaxed">
                    {showFullBank ? (
                      <>
                        <strong className="text-amber-700">Currently ON.</strong> Customers can wire-transfer directly using the
                        details printed on the PDF. Recommended only if you accept wire-transfer payments.
                      </>
                    ) : (
                      <>
                        <strong className="text-emerald-700">Currently OFF (recommended).</strong> PDFs show only the bank name +
                        last 4 digits — enough for audit/confirmation but not enough for a third party to wire to your account.
                      </>
                    )}
                  </p>
                </div>
                {policySaving && <Loader size={14} className="animate-spin text-slate-500 mt-1" />}
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs font-ibm text-slate-500 flex items-start gap-2" data-testid="platform-bank-disclaimer">
          <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
          <p>
            Bank details are encrypted at rest by your database provider and never shown to other organizations. Only the
            bank name and last 4 digits are visible to subscribers via the in-app payout confirmation banner.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PlatformBankSettings;
